import { Input } from '@coolblue-development/freddy';
import enterApplicationDetailsTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/enterApplicationDetailsTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { Rules } from '@sharedTypes';
import { Controller, useFormContext } from 'react-hook-form';
import { isEmail, isMobilePhone } from 'validator';

import { FormLabelWithInfo } from './FormLabelWithInfo';

export type TextInputHelperProps = {
  name: string;
  type: 'text' | 'email' | 'phonenumber' | 'date';
  rules?: Rules;
  label: string;
  placeholder?: string;
  disable?: boolean;
  info?: string;
};

export const TextInputHelper = ({
  name,
  type,
  rules = {},
  label,
  placeholder = '',
  disable = false,
  info
}: TextInputHelperProps) => {
  const {
    control,
    formState: { errors }
  } = useFormContext();

  const { trackEvent } = useAnalytics();
  const errorMessage = errors[name]?.message;
  if (errorMessage) {
    trackEvent(
      enterApplicationDetailsTrigger({
        input_field: label,
        status: 'failure',
        content: errorMessage.toString()
      })
    );
  }
  const isSingleAlphaNumeric = (inputString: string) => {
    const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    const segments = [...seg.segment(inputString)].map(g => g.segment);
    if (segments.length !== 1) return true;
    return /^(?:\p{L}|\p{N})\p{M}*$/u.test(inputString);
  };

  const { validate, ...otherRules } = { ...rules, required: rules.required?.message };
  const emailRule = type === 'email' && {
    emailRule: (value: string) => (value && isEmail(value)) || validate?.message
  };
  const phoneNumberRule = type === 'phonenumber' && {
    phoneNumberRule: (value: string) => (value && isMobilePhone(value)) || validate?.message
  };

  const futureDateRule = type === 'date' && {
    futureDateRule: (value: string) => {
      if (!otherRules.futureDate || !value) return true;
      const today = new Date().toISOString().split('T')[0];
      return value > today || otherRules.futureDate.message;
    }
  };

  const singleEmojiRule = {
    singleEmojiRule: (value: string) => {
      if (!otherRules.singleEmoji || !value) {
        return true;
      }

      return isSingleAlphaNumeric(value.trim()) || otherRules.singleEmoji.message;
    }
  };

  const validationRules = {
    ...otherRules,
    validate: {
      ...emailRule,
      ...phoneNumberRule,
      ...singleEmojiRule,
      ...futureDateRule
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=''
      rules={validationRules}
      render={value => (
        <FormLabelWithInfo label={label} info={info} htmlFor={name}>
          <Input
            {...value.field}
            id={name}
            type={type === 'email' ? 'email' : type === 'date' ? 'date' : 'text'}
            validation={value.fieldState.error ? 'error' : undefined}
            placeholder={placeholder}
            onFocus={() => {
              trackEvent(
                enterApplicationDetailsTrigger({
                  input_field: label,
                  status: 'focus',
                  content: ''
                })
              );
            }}
            helperText={value.fieldState.error?.message}
            disabled={disable}
          />
        </FormLabelWithInfo>
      )}
    />
  );
};
